import { ReadingIntentionsKey } from '®shared/annotations';
import { SaveableFactory } from '®shared/data';

import {
  UserAnnotationsActionTypes,
  UserAnnotationsActions,
} from './user-annotations.actions';
import { UserAnnotationsState } from './user-annotations.state';

export function userAnnotationsReducer(
  state: UserAnnotationsState,
  action: UserAnnotationsActions,
): UserAnnotationsState {
  switch (action.type) {
    case UserAnnotationsActionTypes.USER_ANNOTATIONS_FOR_LIST_LOADED: {
      const {
        listId,
        userId,
        readingIntentionsByResource,
        studyNotesByResource,
      } = action.payload;
      return {
        ...state,
        readingIntentionsByUserAndResource: {
          ...state.readingIntentionsByUserAndResource,
          [userId]: {
            ...state.readingIntentionsByUserAndResource[userId],
            ...readingIntentionsByResource,
          },
        },
        studyNotesByUserAndResource: {
          ...state.studyNotesByUserAndResource,
          [userId]: {
            ...state.studyNotesByUserAndResource[userId],
            ...studyNotesByResource,
          },
        },
        listsLoadedByUser: {
          ...state.listsLoadedByUser,
          [userId]: { ...state.listsLoadedByUser[userId], [listId]: true },
        },
      };
    }
    case UserAnnotationsActionTypes.USER_ANNOTATIONS_FOR_LIST_LOADING_FAILED: {
      const { listId, userId } = action.payload;
      return {
        ...state,
        listsLoadedByUser: {
          ...state.listsLoadedByUser,
          [userId]: { ...state.listsLoadedByUser[userId], [listId]: false },
        },
      };
    }
    case UserAnnotationsActionTypes.UPDATE_READING_INTENTION: {
      const { userId, resourceId, status } = action.payload;
      return {
        ...state,
        readingIntentionsByUserAndResource: {
          ...state.readingIntentionsByUserAndResource,
          [userId]: {
            ...state.readingIntentionsByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.saving(),
              status,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.UPDATE_READING_INTENTION_SUCCEEDED: {
      const { userId, resourceId, status } = action.payload;
      return {
        ...state,
        readingIntentionsByUserAndResource: {
          ...state.readingIntentionsByUserAndResource,
          [userId]: {
            ...state.readingIntentionsByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.reset(),
              status,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.UPDATE_READING_INTENTION_FAILED: {
      const { userId, resourceId, status, error } = action.payload;
      return {
        ...state,
        readingIntentionsByUserAndResource: {
          ...state.readingIntentionsByUserAndResource,
          [userId]: {
            ...state.readingIntentionsByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.errorSaving(error),
              status,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.DELETE_READING_INTENTION: {
      const { userId, resourceId } = action.payload;
      const status = getCurrentReadingIntention(state, userId, resourceId);

      return {
        ...state,
        readingIntentionsByUserAndResource: {
          ...state.readingIntentionsByUserAndResource,
          [userId]: {
            ...state.readingIntentionsByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.deleting(),
              status,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.DELETE_READING_INTENTION_SUCCEEDED: {
      const { userId, resourceId } = action.payload;
      return {
        ...state,
        readingIntentionsByUserAndResource: {
          ...state.readingIntentionsByUserAndResource,
          [userId]: {
            ...state.readingIntentionsByUserAndResource[userId],
            [resourceId]: undefined,
          },
        },
      };
    }
    case UserAnnotationsActionTypes.DELETE_READING_INTENTION_FAILED: {
      const { userId, resourceId, error } = action.payload;
      const status = getCurrentReadingIntention(state, userId, resourceId);

      return {
        ...state,
        readingIntentionsByUserAndResource: {
          ...state.readingIntentionsByUserAndResource,
          [userId]: {
            ...state.readingIntentionsByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.errorDeleting(error),
              status,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.UPDATE_STUDY_NOTE: {
      const { userId, resourceId, note } = action.payload;
      return {
        ...state,
        studyNotesByUserAndResource: {
          ...state.studyNotesByUserAndResource,
          [userId]: {
            ...state.studyNotesByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.saving(),
              note,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.UPDATE_STUDY_NOTE_SUCCEEDED: {
      const { userId, resourceId, note } = action.payload;
      return {
        ...state,
        studyNotesByUserAndResource: {
          ...state.studyNotesByUserAndResource,
          [userId]: {
            ...state.studyNotesByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.reset(),
              note,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.UPDATE_STUDY_NOTE_FAILED: {
      const { userId, resourceId, note, error } = action.payload;
      return {
        ...state,
        studyNotesByUserAndResource: {
          ...state.studyNotesByUserAndResource,
          [userId]: {
            ...state.studyNotesByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.errorSaving(error),
              note,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.DELETE_STUDY_NOTE: {
      const { userId, resourceId } = action.payload;
      const note = getCurrentStudyNote(state, userId, resourceId);

      return {
        ...state,
        studyNotesByUserAndResource: {
          ...state.studyNotesByUserAndResource,
          [userId]: {
            ...state.studyNotesByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.deleting(),
              note,
            },
          },
        },
      };
    }
    case UserAnnotationsActionTypes.DELETE_STUDY_NOTE_SUCCEEDED: {
      const { userId, resourceId } = action.payload;
      return {
        ...state,
        studyNotesByUserAndResource: {
          ...state.studyNotesByUserAndResource,
          [userId]: {
            ...state.studyNotesByUserAndResource[userId],
            [resourceId]: undefined,
          },
        },
      };
    }
    case UserAnnotationsActionTypes.DELETE_STUDY_NOTE_FAILED: {
      const { userId, resourceId, error } = action.payload;
      const note = getCurrentStudyNote(state, userId, resourceId);

      return {
        ...state,
        studyNotesByUserAndResource: {
          ...state.studyNotesByUserAndResource,
          [userId]: {
            ...state.studyNotesByUserAndResource[userId],
            [resourceId]: {
              ...SaveableFactory.errorDeleting(error),
              note,
            },
          },
        },
      };
    }
    default: {
      return state;
    }
  }
}

function getCurrentReadingIntention(
  state: UserAnnotationsState,
  userId: string,
  resourceId: string,
): ReadingIntentionsKey | undefined {
  const { readingIntentionsByUserAndResource } = state;
  const readingIntentionsByResource =
    readingIntentionsByUserAndResource[userId];

  if (!readingIntentionsByResource) {
    return undefined;
  }

  const readingIntention = readingIntentionsByResource[resourceId];
  if (!readingIntention) {
    return undefined;
  }

  return readingIntention.status;
}

function getCurrentStudyNote(
  state: UserAnnotationsState,
  userId: string,
  resourceId: string,
): string | undefined {
  const { studyNotesByUserAndResource } = state;
  const studyNotesByResource = studyNotesByUserAndResource[userId];

  if (!studyNotesByResource) {
    return undefined;
  }

  const readingIntention = studyNotesByResource[resourceId];
  if (!readingIntention) {
    return undefined;
  }

  return readingIntention.note;
}
